import React, { useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useUIStore, useDomainStore } from '@/hooks/useStores';
import { api } from '@/api';
import appConfig from '@/config/app';
import { getAccessToken } from '@/services/SessionService';
import { useImportStatusPolling } from '@/hooks/useImportStatusPolling';
import { validateGedcomFile } from '@/utils/fileValidation';
import { canUploadGedcom } from '@/utils/rolePermissions';
import GedcomUploadButton from '@/components/shared/GedcomUploadButton';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import './gedcom-upload.css';

const GedcomUploadScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const { t: tCommon } = useTranslation('common');
  const { t: tForms } = useTranslation('forms');
  const { isAuthenticated } = useAuth();
  const uiStore = useUIStore();
  const domainStore = useDomainStore();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importStatus = useImportStatusPolling();
  const { familyId: familyIdParam } = useParams<{ familyId: string }>();

  useEffect(() => {
    if (familyIdParam) {
      domainStore.setSelectedFamily(familyIdParam);
    }
  }, [familyIdParam, domainStore]);

  if (!isAuthenticated) {
    return (
      <div className="gedcom-upload-page__auth-message">
        <h2>{t('uploadGedcom.signInRequired')}</h2>
      </div>
    );
  }

  const selectedFamily = domainStore.currentFamily || (familyIdParam ? { id: familyIdParam } : null);

  if (!selectedFamily) {
    return (
      <div className="gedcom-upload-page gedcom-upload-page--centered">
        <section className="gedcom-upload-page__section gedcom-upload-page__section--message">
          <h2 className="gedcom-upload-page__section-title">{t('uploadGedcom.noFamilyTitle')}</h2>
          <p className="gedcom-upload-page__body">{t('uploadGedcom.noFamilyBody')}</p>
          <Link to="/families" className="btn btn--primary">
            {t('families.title')}
          </Link>
        </section>
      </div>
    );
  }

  const userRole = domainStore.currentFamily?.userRole;
  const hasUploadPermission = canUploadGedcom(userRole);

  if (!hasUploadPermission) {
    return (
      <div className="gedcom-upload-page gedcom-upload-page--centered">
        <section className="gedcom-upload-page__section gedcom-upload-page__section--message">
          <h2 className="gedcom-upload-page__section-title">{t('uploadGedcom.accessDeniedTitle')}</h2>
          <p className="gedcom-upload-page__body">{t('uploadGedcom.accessDeniedBody')}</p>
          <Link to="/families" className="btn btn--primary">
            {tForms('actions.backToFamilies')}
          </Link>
        </section>
      </div>
    );
  }

  const memberCount = domainStore.currentFamily?.memberCount ?? 0;
  if (memberCount > 0) {
    return (
      <div className="gedcom-upload-page gedcom-upload-page--centered">
        <section className="gedcom-upload-page__section gedcom-upload-page__section--message">
          <h2 className="gedcom-upload-page__section-title">{t('uploadGedcom.unavailableTitle')}</h2>
          <p className="gedcom-upload-page__body">{t('uploadGedcom.unavailableBody', { count: memberCount })}</p>
          {familyIdParam && (
            <Link to={`/families/${familyIdParam}/overview`} className="btn btn--primary">
              {t('uploadGedcom.backToOverview')}
            </Link>
          )}
        </section>
      </div>
    );
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      const validation = validateGedcomFile(selectedFile);
      if (!validation.valid) {
        uiStore.setUploadErrorMessage(validation.error || t('uploadGedcom.errors.invalidFile'));
        uiStore.setUploadFile(null);
        return;
      }

      uiStore.setUploadFile(selectedFile);
      uiStore.setUploadErrorMessage(null);
      uiStore.setUploadState('idle');
    }
  };

  const handleUpload = async () => {
    if (!uiStore.uploadFile || !selectedFamily) return;

    uiStore.setUploadState('uploading');
    uiStore.setUploadErrorMessage(null);

    try {
      const uploadRequest = {
        fileName: uiStore.uploadFile.name,
        fileSize: uiStore.uploadFile.size
      };

      const importResult = await api.gedcomImports.createGedcomImport(selectedFamily.id, uploadRequest);

      if (!importResult.success) {
        // eslint-disable-next-line no-console
        console.error('Failed to create import job:', importResult.error.getUserMessage());

        if (importResult.error.isAuthError()) {
          throw new Error(t('uploadGedcom.errors.signInAgain'));
        } else if (importResult.error.code === 'NOT_FOUND') {
          throw new Error(t('uploadGedcom.errors.familyNotFound'));
        } else if (importResult.error.isValidationError()) {
          throw new Error(t('uploadGedcom.errors.invalidFileInfo'));
        } else if (importResult.error.isNetworkError()) {
          throw new Error(t('uploadGedcom.errors.network'));
        } else {
          throw new Error(importResult.error.getUserMessage());
        }
      }

      const importResponse = importResult.data;

      let uploadResponse;

      if (importResponse.upload.method === 'POST') {
        const formData = new FormData();
        formData.append('file', uiStore.uploadFile);

        uploadResponse = await fetch(`${appConfig.apiUrl}${importResponse.upload.url}`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${await getAccessToken()}`
          },
          body: formData
        });
      } else {
        uploadResponse = await fetch(importResponse.upload.url, {
          method: 'PUT',
          headers: {
            'Content-Type': importResponse.upload.headers?.['Content-Type'] || 'text/plain'
          },
          body: uiStore.uploadFile
        });
      }

      if (!uploadResponse.ok) {
        throw new Error(
          t('uploadGedcom.errors.uploadFailedStatus', {
            status: uploadResponse.status,
            statusText: uploadResponse.statusText
          })
        );
      }

      const completeResult = await api.gedcomImports.completeGedcomImport(selectedFamily.id, importResponse.jobId);

      if (!completeResult.success) {
        // eslint-disable-next-line no-console
        console.error('Failed to complete import:', completeResult.error.getUserMessage());

        if (completeResult.error.isAuthError()) {
          throw new Error(t('uploadGedcom.errors.signInComplete'));
        } else if (completeResult.error.code === 'NOT_FOUND') {
          throw new Error(t('uploadGedcom.errors.jobNotFound'));
        } else if (completeResult.error.isNetworkError()) {
          throw new Error(t('uploadGedcom.errors.networkComplete'));
        } else {
          throw new Error(completeResult.error.getUserMessage());
        }
      }

      uiStore.setUploadState('success');
      uiStore.setActiveImport(importResponse.jobId, selectedFamily.id);
    } catch (error) {
      uiStore.setUploadState('error');

      const errorMessage = error instanceof Error ? error.message : t('uploadGedcom.errors.uploadFailedGeneric');

      uiStore.setUploadErrorMessage(errorMessage);

      // eslint-disable-next-line no-console
      console.error('GEDCOM upload error:', error);
    }
  };

  const handleReset = () => {
    uiStore.resetUploadState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancel = () => {
    uiStore.resetUploadState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    try {
      navigate(-1);
    } catch {
      navigate('/families');
    }
  };

  const isBusy = uiStore.uploadState === 'uploading' || !!uiStore.activeImportJobId;
  const sizeMb = uiStore.uploadFile ? (uiStore.uploadFile.size / 1024 / 1024).toFixed(2) : '';

  return (
    <div className="gedcom-upload-page">
      <header className="gedcom-upload-page__header">
        <h1 className="gedcom-upload-page__title">{t('uploadGedcom.title')}</h1>
        <p className="gedcom-upload-page__lede">
          {t('uploadGedcom.lede')} <ScreenHelpDot />
        </p>
      </header>

      <section className="gedcom-upload-page__section">
        <div className="gedcom-upload-page__field">
          <label htmlFor="gedcom-file" className="gedcom-upload-page__label">
            {t('uploadGedcom.fileLabel')}
          </label>
          <input
            ref={fileInputRef}
            id="gedcom-file"
            type="file"
            accept=".ged,.gdz"
            onChange={handleFileChange}
            className="gedcom-upload-page__input"
          />
        </div>

        {uiStore.uploadFile && (
          <div className="gedcom-upload-page__file-info">
            <strong>{t('uploadGedcom.selectedFile')}</strong> {uiStore.uploadFile.name}
            <br />
            <strong>{t('uploadGedcom.size')}</strong> {t('uploadGedcom.sizeMb', { size: sizeMb })}
          </div>
        )}

        {uiStore.uploadErrorMessage && (
          <div className="gedcom-upload-page__message gedcom-upload-page__message--error" role="alert">
            {uiStore.uploadErrorMessage}
          </div>
        )}

        {uiStore.uploadState === 'success' && !uiStore.activeImportJobId && (
          <div className="gedcom-upload-page__message gedcom-upload-page__message--success" role="status">
            {t('uploadGedcom.success')}
          </div>
        )}

        <div className="gedcom-upload-page__actions">
          <button type="button" onClick={handleCancel} disabled={isBusy} className="btn btn--ghost">
            {tCommon('cancel')}
          </button>

          <GedcomUploadButton
            onClick={handleUpload}
            disabled={!uiStore.uploadFile || isBusy}
            uploadState={uiStore.uploadState}
            hasActiveImport={!!uiStore.activeImportJobId}
            importStatus={importStatus}
          />

          {uiStore.uploadFile && (
            <button type="button" onClick={handleReset} disabled={isBusy} className="btn btn--text">
              {tForms('actions.reset')}
            </button>
          )}
        </div>
      </section>
    </div>
  );
});

export default GedcomUploadScreen;
